import React, { useMemo, useState } from 'react'
import {
  useCrosswalk,
  useFrameworkForId,
} from 'domains/scope-and-sequence/hooks'
import { ValidPercentage } from 'domains/scope-and-sequence/components'
import {
  Button,
  IconButton,
  Tooltip,
  TextField,
  TooltipProps,
} from '@mui/material'
import { Table } from 'shared/ui/Table/Table'
import { CrosswalkMappingSetType } from 'domains/scope-and-sequence/types'
import { useSortableColumns } from 'components/resources/resourceList/tableUtility'
import { Column } from 'shared/ui/Table/types'
import styled from '@emotion/styled'
import { TablePagination } from 'shared/ui/Table/TablePagination'
import { exportCsv } from 'shared/utils'
import { ConfirmDialog } from 'shared/ui'
import { EditCrosswalkModal } from './EditCrosswalkModal'
import { PercentStatusBar, getAlignmentColor } from './PercentStatusBar'
import {
  Edit,
  Notes,
  Download,
  Add,
  Cancel,
  CheckCircle,
  Delete,
} from '@mui/icons-material'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  listDataAtom,
  resourceKeyAtom,
  resourceUpdateMap,
} from 'components/resources/resourceList/atoms'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'
import { doAdminQueriesWithResult } from 'providers/mutations'

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))`
  & .MuiTooltip-tooltip {
    background: #3e4b4b;
    color: white;
    font-size: 14px;
    line-height: 18px;
    padding: 4px 12px;
    border-radius: 4px;
    position: relative;
  }
`

export function Crosswalk({ id }: { id: number | string }) {
  const { crosswalk, refetch } = useCrosswalk(Number(id))
  const sourceFramework = useFrameworkForId(crosswalk?.source_framework_id)
  const targetFramework = useFrameworkForId(crosswalk?.target_framework_id)
  useSetAtom(resourceKeyAtom)('crosswalkMappingSets')
  const reFetchHome = useSetAtom(listDataAtom)
  const doDelete = useWithErrorNotification(
    { errorTitle: `Error Deleting Alignment` },
    doAdminQueriesWithResult,
  )

  const [deleting, setDeleting] = useState<
    false | NonNullable<CrosswalkMappingSetType['alignments']>[number]['id']
  >(false)
  const [editing, setEditing] = useState<
    false | Partial<NonNullable<CrosswalkMappingSetType['alignments']>[number]>
  >(false)
  const { columns, sorted } = useColumns(
    crosswalk?.alignments,
    setDeleting,
    setEditing,
  )
  const [search, setSearch] = useState<string>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const sortedItems = useMemo(
    () =>
      search
        ? sorted.filter(
            (x) =>
              x.source_standard_code
                .toLowerCase()
                .includes(search.toLowerCase()) ||
              x.target_standard_code
                .toLowerCase()
                .includes(search.toLowerCase()),
          )
        : sorted,
    [search, sorted],
  )

  if (!crosswalk || !sourceFramework || !targetFramework) {
    return (
      <div
        style={{
          display: 'flex',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <h1>Loading...</h1>
      </div>
    )
  }

  const data =
    rowsPerPage > 0 // shallow copy of part of the array
      ? sortedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sortedItems

  return (
    <main style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <ConfirmDialog
        title={`Delete Alignment #${deleting}`}
        onClose={() => setDeleting(false)}
        onConfirm={async () => {
          await doDelete([
            {
              resourceData: {
                key: resourceUpdateMap.standardAlignments.mutationDeleteKey,
              },
              params: { id: deleting },
            },
          ])
          refetch()
          reFetchHome()
          setDeleting(false)
        }}
        open={!!deleting}
        destructive
      >
        <div style={{ textAlign: 'center' }}>
          <h2>Are you sure?</h2>
          <h3>This action is not reversible</h3>
        </div>
      </ConfirmDialog>
      <EditCrosswalkModal
        row={editing}
        setRow={setEditing}
        crosswalkId={crosswalk?.id}
        onSaved={refetch}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <header>
          <h1>{crosswalk.name}</h1>
          <p>{crosswalk.description}</p>
        </header>
      </div>
      <div
        style={{
          border: '2px solid #4caf50',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
          backgroundColor: '#e8f5e9',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ display: 'flex', gap: '10px', margin: 0 }}>
              {sourceFramework.name}
              <span>({sourceFramework.jurisdiction})</span>
              <ValidPercentage
                param={'source_valid'}
                alignments={crosswalk.alignments}
              />
            </h2>
          </div>
          <span style={{ fontSize: '30px' }}>→</span>
          <div>
            <h2 style={{ display: 'flex', gap: '10px', margin: 0 }}>
              {targetFramework.name}
              <span>({targetFramework.jurisdiction})</span>
              <ValidPercentage
                param={'target_valid'}
                alignments={crosswalk.alignments}
              />
            </h2>
          </div>
        </div>
        <p style={{ margin: '8px 0 0', color: '#555', fontSize: '14px' }}>
          Validity percentage indicates if standard codes from your crosswalk
          match those in our Kiddom Standards database. If you see invalid
          codes, use SEARCH KIDDOM STANDARDS to find the correct format, then
          click the edit button to correct them.
        </p>
      </div>
      <PercentStatusBar alignments={crosswalk.alignments} />
      <Table
        title={`Standard Alignments`}
        columns={columns}
        items={data}
        toolBarItems={[
          <div
            key="options"
            style={{ display: 'flex', gap: '10px', flexGrow: 1 }}
          >
            <TextField
              key="search"
              size="small"
              placeholder={'Filter Standard Codes (case-insensitive)'}
              style={{ width: '20rem' }}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              key="add"
              variant="contained"
              onClick={() => setEditing({})}
              style={{ display: 'flex', minWidth: 'fit-content', gap: '.5rem' }}
            >
              <Add /> Add
            </Button>
            <Button
              key="download"
              variant="contained"
              onClick={() =>
                downloadAlignments(crosswalk.name, crosswalk.alignments)
              }
              style={{ display: 'flex', minWidth: 'fit-content', gap: '.5rem' }}
            >
              <Download /> Download
            </Button>
          </div>,
        ]}
        footer={
          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={sortedItems.length}
            onRowsPerPageChange={setRowsPerPage}
            onPageChange={setPage}
          />
        }
      />
    </main>
  )
}

function useColumns(
  data: CrosswalkMappingSetType['alignments'] = [],
  setDeleting: (id: number) => void,
  setEditing: (
    row: NonNullable<CrosswalkMappingSetType['alignments']>[number],
  ) => void,
) {
  const columns: Column<
    NonNullable<CrosswalkMappingSetType['alignments']>[number]
  >[] = useMemo(
    () => [
      {
        width: 40,
        title: 'ID',
        rowContent: (row) => row.id,
      },
      {
        width: 300,
        title: 'Source Standard',
        sortable: 'source_standard_code',
        rowContent: (row) =>
          row.source_description ? (
            <StyledTooltip title={row.source_description}>
              <span style={{ textDecoration: 'underline' }}>
                {row.source_standard_code}
              </span>
            </StyledTooltip>
          ) : (
            row.source_standard_code
          ),
      },
      {
        width: 300,
        title: 'Target Standard',
        sortable: 'target_standard_code',
        rowContent: (row) =>
          row.target_description ? (
            <StyledTooltip title={row.target_description}>
              <span style={{ textDecoration: 'underline' }}>
                {row.target_standard_code}
              </span>
            </StyledTooltip>
          ) : (
            row.target_standard_code
          ),
      },
      {
        width: 40,
        title: 'Valid?',
        sortable: 'target_valid',
        rowContent: (row) => <ValidIcon valid={row.target_valid} />,
      },
      {
        width: 50,
        title: 'Rating',
        sortable: 'rating',
        rowContent: (row) => row.rating,
      },
      {
        width: 80,
        title: 'Type',
        sortable: 'alignment_type',
        rowContent: (row) => (
          <span
            style={{
              textTransform: 'capitalize',
              fontWeight: 'bold',
              color: getAlignmentColor(row.alignment_type),
            }}
          >
            {row.alignment_type}
          </span>
        ),
      },
      {
        width: 40,
        title: 'Note',
        sortable: 'note',
        rowContent: (row) =>
          row.note ? (
            <StyledTooltip title={row.note} enterTouchDelay={0}>
              <Notes />
            </StyledTooltip>
          ) : (
            ''
          ),
      },
      {
        width: 80,
        title: '',
        rowContent: (row) => (
          <span style={{ display: 'flex', gap: '2px' }}>
            <StyledTooltip title={'Edit'}>
              <IconButton color="primary" onClick={() => setEditing(row)}>
                <Edit />
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title={'Delete'}>
              <IconButton color="error" onClick={() => setDeleting(row.id)}>
                <Delete />
              </IconButton>
            </StyledTooltip>
          </span>
        ),
      },
    ],
    [setDeleting, setEditing],
  )

  return useSortableColumns(data ?? [], columns)
}

function downloadAlignments(
  title: string,
  alignments: CrosswalkMappingSetType['alignments'] = [],
) {
  const titles = [
    'source standard',
    'source standard description',
    'source standard valid',
    'target standard',
    'target standard description',
    'target standard valid',
    'rating',
    'type',
    'note',
  ]

  const data: string[][] = [
    titles,
    ...alignments.map((a) => [
      a.source_standard_code,
      a.source_description ?? '',
      a.source_valid ? 'TRUE' : 'FALSE',
      a.target_standard_code,
      a.target_description ?? '',
      a.target_valid ? 'TRUE' : 'FALSE',
      a.rating.toString(),
      a.alignment_type,
      a.note ?? '',
    ]),
  ]

  exportCsv(data, title)
}

function ValidIcon({ valid }: { valid: boolean }) {
  return valid ? (
    <CheckCircle sx={{ color: '#007B4B' }} />
  ) : (
    <Cancel sx={{ color: '#d32f2f' }} />
  )
}
